import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import FilterOptions from './components/FilterOptions';
import { checkinService, type CheckinHistory, getCheckinImageSrc } from '../../api/checkin';
import {
  Navigation2,
  Search,
  RefreshCw,
  Filter,
  Eye,
  Calendar,
  Clock,
  Car,
  User,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  ImageIcon,
  MapPin,
  Timer,
  StickyNote,
  Info,
  FileText,
} from 'lucide-react';
import {
  PageHeader,
  Button,
  Badge,
  Input,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

const HistoricoDeCheckIn = () => {
  const { setPageTitle } = useAuth();
  const [history, setHistory] = useState<CheckinHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});
  const [selectedHistory, setSelectedHistory] = useState<CheckinHistory | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  useEffect(() => {
    setPageTitle('Histórico de check-ins do setor');
  }, [setPageTitle]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await checkinService.getHistory();
      setHistory(data);
    } catch (_err) {
      setError('Erro ao buscar histórico de check-ins.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchHistory();
  }, [fetchHistory]);

  const filterCategories = useMemo(
    () => ({
      Ação: Array.from(new Set(history.map((item) => item.action)))
        .filter(Boolean)
        .map((action) => ({ id: action, label: action })),
    }),
    [history],
  );

  const filteredHistory = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const selectedActions = appliedFilters.Ação || [];

    return history.filter((h) => {
      const matchesSearch =
        !term ||
        String(h.id).includes(term) ||
        String(h.checkin_id).includes(term) ||
        (h.checkin?.employee?.name || '').toLowerCase().includes(term) ||
        (h.checkin?.employee?.cpf || '').toLowerCase().includes(term) ||
        (h.checkin?.vehicle?.description || '').toLowerCase().includes(term) ||
        (h.checkin?.vehicle?.license_plate || '').toLowerCase().includes(term) ||
        (h.action || '').toLowerCase().includes(term) ||
        (h.details || '').toLowerCase().includes(term) ||
        (h.checkin?.notes || '').toLowerCase().includes(term);

      const matchesAction =
        selectedActions.length === 0 || selectedActions.includes(h.action);

      return matchesSearch && matchesAction;
    });
  }, [history, searchTerm, appliedFilters]);

  const totalPages = Math.max(1, Math.ceil(filteredHistory.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredHistory.length, currentPage, totalPages]);

  const { historyPaginado, pageRange } = useMemo(() => {
    const total = filteredHistory.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      historyPaginado: filteredHistory.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredHistory, currentPage, totalPages]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const ThumbnailImage = ({
    src,
    alt,
    onZoom,
  }: {
    src: string | null;
    alt: string;
    onZoom: () => void;
  }) => {
    if (!src) {
      return (
        <span className="inline-flex items-center rounded-md bg-slate-50 px-2 py-1 text-[11.5px] font-medium text-slate-400 ring-1 ring-inset ring-slate-200 whitespace-nowrap">
          <ImageIcon className="h-3 w-3 stroke-[1.9] mr-1" />
          Sem foto
        </span>
      );
    }
    return (
      <button
        type="button"
        onClick={onZoom}
        className="group relative block h-10 w-10 shrink-0 overflow-hidden rounded-lg border-2 border-white shadow-sm transition-all hover:z-10 hover:scale-110 hover:shadow-md"
        title={`Clique para ampliar a foto de ${alt}`}
      >
        <img src={src} alt={alt} className="h-full w-full object-cover" />
      </button>
    );
  };

  const formatDateOnly = (value?: string) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleDateString('pt-BR');
    } catch {
      return value;
    }
  };

  const formatTimeOnly = (value?: string) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (value?: string) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleString('pt-BR');
    } catch {
      return value;
    }
  };

  const AcaoBadge = ({ acao }: { acao: string }) => {
    const a = (acao || '').toLowerCase();
    if (a.includes('arquiv') || a.includes('finaliz') || a.includes('encerr')) {
      return (
        <Badge tone="emerald" className="gap-1">
          {acao}
        </Badge>
      );
    }
    if (a.includes('cri') || a.includes('check')) {
      return (
        <Badge tone="blue" className="gap-1">
          {acao}
        </Badge>
      );
    }
    if (a.includes('atualiz') || a.includes('edit')) {
      return (
        <Badge tone="amber" className="gap-1">
          {acao}
        </Badge>
      );
    }
    if (a.includes('cancel') || a.includes('remov') || a.includes('delet')) {
      return (
        <Badge tone="red" className="gap-1">
          {acao}
        </Badge>
      );
    }
    return (
      <Badge tone="slate" className="gap-1">
        {acao}
      </Badge>
    );
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedHistory(null);
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Histórico de check-ins do setor" hideTitleInBody />

        <div className="mb-7 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5 max-w-2xl">
            <p className="text-[15px] font-semibold leading-tight text-sky-600">
              Histórico — Check-In / Check-Out
            </p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight leading-tight text-slate-900">
                Trilha completa de todas as ações
              </h1>
              <span className="text-[18px] sm:text-[20px] font-medium text-slate-500 leading-tight">
                de entrada e saída dos veículos
              </span>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
            <Button
              variant="secondary"
              onClick={() => setIsFilterOpen(true)}
              className="h-10 px-4"
            >
              <Filter className="h-4 w-4 stroke-[1.9]" />
              Filtros
            </Button>
            <Button
              variant="secondary"
              onClick={() => void fetchHistory()}
              className="h-10 px-4"
            >
              <RefreshCw className="h-4 w-4 stroke-[1.8]" />
              Atualizar
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 inline-flex items-start gap-3 w-full">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
            <span>{error}</span>
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 min-w-[44px] min-h-[44px] shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 text-white shadow-[0_8px_22px_-10px_rgba(16,185,129,0.7)]">
                  <Navigation2 className="h-[22px] w-[22px] stroke-[1.9]" />
                </div>
                <div>
                  <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                    Registros de ações de check-in
                  </h2>
                  <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                    Criações, finalizações, arquivamentos e edições
                  </p>
                </div>
              </div>
              <div className="relative w-full sm:w-[360px] shrink-0">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Buscar ID, funcionário, veículo, placa, ação..."
                  className="!pl-10 h-10"
                />
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {loading && history.length === 0 ? (
              <div className="flex items-center justify-center gap-2.5 py-16">
                <Loader2 className="h-5 w-5 animate-spin text-teal-600" />
                <span className="text-[13.5px] font-semibold text-slate-600">
                  Carregando histórico...
                </span>
              </div>
            ) : (
              <ModernTable>
                <Thead>
                  <Tr>
                    <Th className="w-[11%] min-w-[150px]">Data</Th>
                    <Th className="w-[10%] min-w-[130px]">Ação</Th>
                    <Th className="w-[18%] min-w-[220px]">Funcionário</Th>
                    <Th className="w-[18%] min-w-[220px]">Veículo</Th>
                    <Th className="w-[9%] min-w-[110px]">Check-in</Th>
                    <Th className="w-[9%] min-w-[110px]">Check-out</Th>
                    <Th className="w-[4%] min-w-[80px]">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {historyPaginado.length === 0 ? (
                    <Tr>
                      <Td colSpan={7}>
                        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                            <Navigation2 className="h-7 w-7 stroke-[1.8]" />
                          </div>
                          <div className="text-[14px] font-bold text-slate-700">
                            Nenhum registro no histórico
                          </div>
                          <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                            {searchTerm || appliedFilters.Ação?.length
                              ? 'Nenhum resultado para os filtros aplicados. Tente outros termos.'
                              : 'As ações de check-in aparecerão aqui automaticamente.'}
                          </p>
                        </div>
                      </Td>
                    </Tr>
                  ) : (
                      historyPaginado.map((h) => {
                        const checkinImgObj = h.checkin?.images?.find((img) => img.type === 'CHECKIN');
                        const checkoutImgObj = h.checkin?.images?.find((img) => img.type === 'CHECKOUT');
                        const checkinSrc = getCheckinImageSrc(checkinImgObj);
                        const checkoutSrc = getCheckinImageSrc(checkoutImgObj);

                        const employeeFullText = [h.checkin?.employee?.name, h.checkin?.employee?.cpf].filter(Boolean).join(' • CPF: ');
                        const vehicleFullText = [h.checkin?.vehicle?.description, h.checkin?.vehicle?.license_plate].filter(Boolean).join(' • Placa: ');

                        return (
                          <Tr key={h.id}>
                            <Td>
                              <div>
                                <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                  <Calendar className="h-[15px] w-[15px] text-slate-400" />
                                  {formatDateOnly(h.created_at)}
                                </div>
                                <div className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-slate-500 tabular-nums">
                                  <Clock className="h-[13px] w-[13px] text-slate-400" />
                                  {formatTimeOnly(h.created_at)}
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="flex items-center justify-start">
                                {h.action ? <AcaoBadge acao={h.action} /> : '-'}
                              </div>
                            </Td>
                            <Td>
                              <div
                                className="min-w-0 w-full max-w-[260px] overflow-hidden"
                                title={employeeFullText || '-'}
                              >
                                <div className="flex items-start gap-2.5">
                                  <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] min-h-[36px] items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                    <User className="h-[18px] w-[18px] stroke-[1.9]" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="block max-w-[190px] truncate text-[13.5px] font-semibold leading-tight text-slate-900">
                                      {h.checkin?.employee?.name || '-'}
                                    </span>
                                    <div className="mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500 tabular-nums">
                                      {h.checkin?.employee?.cpf || '-'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div
                                className="min-w-0 w-full max-w-[260px] overflow-hidden"
                                title={vehicleFullText || '-'}
                              >
                                <div className="flex items-start gap-2.5">
                                  <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] min-h-[36px] items-center justify-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-inset ring-teal-200">
                                    <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="block max-w-[190px] truncate text-[13.5px] font-semibold leading-tight text-slate-900">
                                      {h.checkin?.vehicle?.description || '-'}
                                    </span>
                                    <div className="mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500 inline-flex items-center gap-1">
                                      <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-slate-600 font-[11px] font-semibold tabular-nums tracking-wide">
                                        {h.checkin?.vehicle?.license_plate || 'Sem placa'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="flex items-center justify-center">
                                <ThumbnailImage
                                  src={checkinSrc}
                                  alt="check-in"
                                  onZoom={() => checkinSrc && setExpandedImage(checkinSrc)}
                                />
                              </div>
                            </Td>
                            <Td>
                              <div className="flex items-center justify-center">
                                <ThumbnailImage
                                  src={checkoutSrc}
                                  alt="check-out"
                                  onZoom={() => checkoutSrc && setExpandedImage(checkoutSrc)}
                                />
                              </div>
                            </Td>
                            <Td>
                              <div className="flex items-center justify-center gap-1.5">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setSelectedHistory(h);
                                    setIsViewModalOpen(true);
                                  }}
                                  title="Visualizar detalhes"
                                  className="h-8 w-8 rounded-lg text-slate-500 hover:text-teal-700 hover:bg-teal-50 hover:ring-teal-200"
                                >
                                  <Eye className="h-4 w-4 stroke-[1.9]" />
                                </Button>
                              </div>
                            </Td>
                          </Tr>
                        );
                      })
                    )
                  }
                </Tbody>
              </ModernTable>
            )}

            {pageRange.total > 0 ? (
              <div className="mt-4 flex flex-col gap-3 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[12.5px] font-medium leading-tight text-slate-500">
                  Mostrando{' '}
                  <span className="font-bold tabular-nums text-slate-800">
                    {pageRange.start}
                  </span>
                  {'–'}{' '}
                  <span className="font-bold tabular-nums text-slate-800">
                    {pageRange.end}
                  </span>
                  {' de '}
                  <span className="font-bold tabular-nums text-slate-800">
                    {pageRange.total}
                  </span>
                  {' registro(s)'}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:ring-offset-1"
                    aria-label="Primeira página"
                  >
                    <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:ring-offset-1"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <div className="inline-flex items-center gap-1 mx-1">
                    {getPageNumbers.map((n, idx) =>
                      n === '...' ? (
                        <span
                          key={`dots-${idx}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setCurrentPage(n)}
                          className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:ring-offset-1 ${
                            n === currentPage
                              ? 'bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 text-white shadow-[0_4px_14px_-4px_rgba(16,185,129,0.55)]'
                              : 'border border-slate-200 bg-white text-slate-700 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700'
                          }`}
                        >
                          {n}
                        </button>
                      )
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:ring-offset-1"
                    aria-label="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:ring-offset-1"
                    aria-label="Última página"
                  >
                    <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </SectionCard>
      </div>

      {isViewModalOpen && selectedHistory && (
        <Modal
          onClose={closeViewModal}
          maxWidthClass="max-w-4xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 min-w-[40px] min-h-[40px] shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 text-white shadow-[0_6px_16px_-8px_rgba(16,185,129,0.65)]">
                <Eye className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                  Detalhes do Check-In — Registro #{selectedHistory.id}
                </div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Check-in ID #{selectedHistory.checkin_id}
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button
                type="button"
                onClick={closeViewModal}
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
              >
                Fechar
              </button>
            </div>
          }
        >
          <div className="space-y-5">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">
                  Informações Gerais
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Data do Registro</span>
                    <span className="text-sm text-slate-900 font-medium tabular-nums">
                      {formatDateTime(selectedHistory.created_at)}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Ação Realizada</span>
                    <div className="flex items-center">
                      {selectedHistory.action ? <AcaoBadge acao={selectedHistory.action} /> : '-'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 md:col-span-2">
                    <span className="text-xs font-semibold text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <Info className="h-3.5 w-3.5 stroke-[1.9] text-slate-400" />
                        Detalhes do Histórico
                      </span>
                    </span>
                    <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-[13px] font-medium leading-relaxed text-slate-800 whitespace-pre-wrap min-h-[50px]">
                      {selectedHistory.details || '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-teal-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-emerald-700">
                  Equipe e Veículo
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Funcionário</span>
                    <p className="text-sm text-slate-900 font-medium">
                      {selectedHistory.checkin?.employee?.name || '-'}
                    </p>
                    <p className="text-xs text-slate-500 tabular-nums">
                      {selectedHistory.checkin?.employee?.cpf || '-'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Veículo</span>
                    <p className="text-sm text-slate-900 font-medium">
                      {selectedHistory.checkin?.vehicle?.description ||
                        '-'}
                    </p>
                    <p className="text-xs text-slate-500 tabular-nums">
                      {selectedHistory.checkin?.vehicle?.license_plate || '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-amber-500 to-orange-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-amber-700">
                  Fotos de Registro
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <span className="text-xs font-semibold text-slate-500">Check-In</span>
                    {(() => {
                      const ciImg = selectedHistory.checkin?.images?.find((img) => img.type === 'CHECKIN');
                      const ciSrc = getCheckinImageSrc(ciImg);
                      return ciSrc ? (
                        <button
                          type="button"
                          onClick={() => setExpandedImage(ciSrc)}
                          className="group relative block w-full overflow-hidden rounded-xl border border-slate-200 shadow-sm transition-all hover:shadow-lg"
                          title="Clique para ampliar"
                        >
                          <div className="aspect-video w-full bg-slate-50">
                            <img
                              src={ciSrc}
                              alt="Foto de check-in"
                              className="h-full w-full object-cover transition-all group-hover:scale-[1.02]"
                            />
                          </div>
                        </button>
                      ) : (
                        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 py-12 text-slate-400">
                          <ImageIcon className="h-10 w-10 stroke-[1.7]" />
                          <span className="text-[13px] font-semibold">Sem foto de check-in</span>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-xs font-semibold text-slate-500">Check-Out</span>
                    {(() => {
                      const coImg = selectedHistory.checkin?.images?.find((img) => img.type === 'CHECKOUT');
                      const coSrc = getCheckinImageSrc(coImg);
                      return coSrc ? (
                        <button
                          type="button"
                          onClick={() => setExpandedImage(coSrc)}
                          className="group relative block w-full overflow-hidden rounded-xl border border-slate-200 shadow-sm transition-all hover:shadow-lg"
                          title="Clique para ampliar"
                        >
                          <div className="aspect-video w-full bg-slate-50">
                            <img
                              src={coSrc}
                              alt="Foto de check-out"
                              className="h-full w-full object-cover transition-all group-hover:scale-[1.02]"
                            />
                          </div>
                        </button>
                      ) : (
                        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 py-12 text-slate-400">
                          <ImageIcon className="h-10 w-10 stroke-[1.7]" />
                          <span className="text-[13px] font-semibold">Sem foto de check-out</span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-indigo-500 to-purple-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-indigo-700">
                  Dados da Viagem / Check-In
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5 stroke-[1.9] text-slate-400" />
                        Destino
                      </span>
                    </span>
                    <p
                      className="text-sm text-slate-900 font-medium truncate max-w-full"
                      title={selectedHistory.checkin?.vehicle?.trips?.[0]?.destination || '-'}
                    >
                      {selectedHistory.checkin?.vehicle?.trips?.[0]?.destination || '-'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5 stroke-[1.9] text-slate-400" />
                        Data da Viagem
                      </span>
                    </span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">
                      {selectedHistory.checkin?.vehicle?.trips?.[0]?.start_date
                        ? formatDateOnly(selectedHistory.checkin.vehicle.trips[0].start_date)
                        : '-'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <Timer className="h-3.5 w-3.5 stroke-[1.9] text-slate-400" />
                        Duração
                      </span>
                    </span>
                    <p className="text-sm text-slate-900 font-bold tabular-nums">
                      {selectedHistory.checkin?.duration != null
                        ? `${selectedHistory.checkin.duration} ${
                            (selectedHistory.details || '').toLowerCase().includes('segundo')
                              ? 'segundos'
                              : 'minutos'
                          }`
                        : '-'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-semibold text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <StickyNote className="h-3.5 w-3.5 stroke-[1.9] text-slate-400" />
                      Notas do Check-In
                    </span>
                  </span>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-3.5 py-3 text-[13px] font-medium leading-relaxed text-slate-800 whitespace-pre-wrap min-h-[60px]">
                    {selectedHistory.checkin?.notes || '-'}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-semibold text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <FileText className="h-3.5 w-3.5 stroke-[1.9] text-slate-400" />
                      Detalhes do Registro
                    </span>
                  </span>
                  <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-[13px] font-medium leading-relaxed text-slate-800 whitespace-pre-wrap min-h-[50px]">
                    {selectedHistory.details || '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {expandedImage && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4 animate-[fadeIn_0.15s_ease-out]"
          onClick={() => setExpandedImage(null)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-inset ring-white/20 backdrop-blur-sm transition-all hover:bg-white/20 hover:scale-105"
            onClick={() => setExpandedImage(null)}
            aria-label="Fechar"
          >
            <X className="h-6 w-6 stroke-[2]" />
          </button>
          <img
            src={expandedImage}
            alt="Foto ampliada"
            className="max-h-[90vh] max-w-[90vw] rounded-2xl border-4 border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <FilterOptions
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApplyFilter={(filters) => {
          setAppliedFilters(filters);
          setCurrentPage(1);
        }}
        filterCategories={filterCategories}
        initialFilters={appliedFilters}
      />
    </div>
  );
};

export default HistoricoDeCheckIn;
